import React from "react";
import { Link } from "react-router-dom";
import { FaFacebookSquare, FaLinkedin, FaInstagramSquare } from "react-icons/fa";
import { FaSquareXTwitter } from "react-icons/fa6";
import {
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineLightningBolt,
} from "react-icons/hi";

/* ─── social links ───────────────────────────────────────────────────────── */
const socials = [
  {
    Icon: FaFacebookSquare,
    label: "Facebook",
    href: "https://facebook.com/electromob",
  },
  {
    Icon: FaLinkedin,
    label: "LinkedIn",
    href: "https://linkedin.com/company/electromob",
  },
  {
    Icon: FaInstagramSquare,
    label: "Instagram",
    href: "https://instagram.com/electromob",
  },
  {
    Icon: FaSquareXTwitter,
    label: "X",
    href: "https://x.com/electromob",
  },
];

/* ─── menu links ─────────────────────────────────────────────────────────── */
const menuLinks = [
  { label: "About",    to: "/Aboutusp" },
  { label: "Team",     to: "/profiles" },
  { label: "Products", to: "/Products" },
  { label: "Contact",  to: "/contact"  },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const Footer = () => {
  return (
    <footer className="text-white">
      <div className="bg-gradient-to-br from-[#102a05] to-[#4a8f0f]">
        <div className="px-6 md:px-10 lg:px-16 py-14 grid gap-12
                        md:grid-cols-2 lg:grid-cols-4">

          {/* ── Brand ─────────────────────────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2">
              <div className="bg-white/15 p-2 rounded-xl">
                <HiOutlineLightningBolt className="size-6" />
              </div>
              <h2 className="font-bold text-xl tracking-wide">ELECTROMOB</h2>
            </div>
            <p className="text-white/80 text-sm mt-4 leading-relaxed">
              Powering a cleaner future with smart electric mobility
              solutions.
            </p>

            {/* social icons — real hrefs, no more "#" */}
            <div className="flex gap-3 mt-6">
              {socials.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Electromob on ${label}`}
                  className="bg-white/10 hover:bg-white hover:text-[#4a8f0f]
                             transition-colors duration-200 p-2 rounded-lg"
                >
                  <Icon className="text-lg" />
                </a>
              ))}
            </div>
          </div>

          {/* ── Menu — use React Router <Link> instead of <a href="#"> ── */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider
                           text-white/90 mb-4">
              Menu
            </h3>
            <ul className="flex flex-col gap-3 text-sm text-white/80">
              {menuLinks.map(({ label, to }) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="hover:text-white hover:pl-1
                               transition-all duration-200"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Contact ───────────────────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider
                           text-white/90 mb-4">
              Contact
            </h3>
            <ul className="flex flex-col gap-3 text-sm text-white/80">
              <li className="flex gap-2">
                <HiOutlineLocationMarker className="text-base mt-0.5 shrink-0" />
                <span>D Close 2 Flat 6, Games Village, Abuja, Nigeria</span>
              </li>
              <li className="flex gap-2 items-center">
                <HiOutlinePhone className="text-base shrink-0" />
                <a
                  href="tel:+2348175262273"
                  className="hover:text-white transition-colors duration-200"
                >
                  +234 817 526 2273
                </a>
              </li>
              <li className="flex gap-2 items-center">
                <HiOutlinePhone className="text-base shrink-0" />
                <a
                  href="tel:+2349116459123"
                  className="hover:text-white transition-colors duration-200"
                >
                  +234 911 645 9123
                </a>
              </li>
              <li className="flex gap-2 items-start">
                <HiOutlineMail className="text-base mt-0.5 shrink-0" />
                <a
                  href="mailto:info@electromob.com.ng"
                  className="break-all hover:text-white transition-colors duration-200"
                >
                  info@electromob.com.ng
                </a>
              </li>
              <li className="flex gap-2 items-start">
                <HiOutlineMail className="text-base mt-0.5 shrink-0" />
                <a
                  href="mailto:erickanjal@electromob.com.ng"
                  className="break-all hover:text-white transition-colors duration-200"
                >
                  erickanjal@electromob.com.ng
                </a>
              </li>
            </ul>
          </div>

          {/* ── Newsletter ────────────────────────────────────────────── */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider
                           text-white/90 mb-4">
              Stay updated
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Get the latest news on electric mobility delivered to your
              inbox.
            </p>
            <form
              className="flex gap-2"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder="Your email"
                className="w-full text-sm px-3 py-2 rounded-lg bg-white/10
                           placeholder-white/60 text-white border border-white/20
                           focus:outline-none focus:border-white/60"
              />
              <button
                type="submit"
                className="bg-white text-[#4a8f0f] font-semibold text-sm
                           px-4 py-2 rounded-lg hover:bg-white/90
                           transition-colors duration-200 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* ── bottom bar ────────────────────────────────────────────────── */}
      <div className="bg-[#102a05]">
        <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between
                        items-center py-4 px-6 md:px-10 lg:px-16
                        text-xs text-white/80">
          <span>© 2026 Electromob. All rights reserved.</span>

          {/* line 128 fix — was href="#", now a real internal route */}
          <Link
            to="/privacy"
            className="hover:text-white transition-colors duration-200"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;